/**
 * Composite current-condition identity for Overview V2 Now.
 *
 * Subject kind is COMPOSITE (not RAE, not Forklift, not CORE_CONDITIONS).
 * Identity: composite_rule_id + evaluated camera/zone/site scope.
 * Current means the latest ci1_ for that subject is OPEN — the same
 * open-incident contract as buyer-live (ts_end IS NULL). Not recurrence.
 */
import type { OverviewConditionRow, OverviewDecisionSummary } from '../continuousSafety/overviewSiteConditionSummary';
import { presentCompositeAlertDetail } from '../incidents/compositeAlertPresentation';
import type { BuyerLiveItem } from '../incidents/liveAlertsReconciliation';

export const COMPOSITE_SUBJECT_KIND = 'COMPOSITE' as const;

export type CompositeCurrentSubject = {
  subjectKind: typeof COMPOSITE_SUBJECT_KIND;
  compositeRuleId: string;
  ruleVersion: string;
  orgId: string;
  siteId: string;
  scopeType: string;
  scopeId: string;
  conditionId: string;
};

const INTERNAL_LOCATION = new Set([
  'worksafety', 'n/a', 'unknown', 'none', 'null', '-', 'shadow_compare', 'experimental_shadow',
]);

function text(value: unknown): string {
  return String(value || '').trim();
}

const CLOSED_COMPOSITE_STATUSES = new Set(['RESOLVED', 'CLOSED', 'ENDED', 'COMPLETE']);

export function isOpenCompositeStatus(status: string): boolean {
  const token = text(status).toUpperCase();
  return token === '' || !CLOSED_COMPOSITE_STATUSES.has(token);
}

export function compositeCurrentSubjectFromItem(item: BuyerLiveItem): CompositeCurrentSubject | null {
  const detail = presentCompositeAlertDetail(item);
  if (!detail) return null;
  const op = item.operational;
  const ruleId = text(op.composite_rule_id);
  if (!ruleId) return null;
  const scopeType = text(op.composite_evaluated_scope_type) || 'camera';
  const scopeId = text(op.composite_evaluated_scope_id) || text(op.camera_id) || text(op.site_id) || 'site';
  const orgId = text(op.organization_id);
  const siteId = text(op.site_id);
  return {
    subjectKind: COMPOSITE_SUBJECT_KIND,
    compositeRuleId: ruleId,
    ruleVersion: text(op.composite_rule_version),
    orgId,
    siteId,
    scopeType,
    scopeId,
    conditionId: ['composite', ruleId, scopeType, scopeId].join(':'),
  };
}

function buyerLocation(item: BuyerLiveItem): string {
  const raw = text(item.operational.composite_location_display);
  if (!raw || INTERNAL_LOCATION.has(raw.toLowerCase())) return 'configured area';
  return raw;
}

function occurrenceTime(item: BuyerLiveItem): string | null {
  return text(item.operational.composite_triggered_at)
    || text(item.operational.recorded_at)
    || text(item.operational.updated_at)
    || null;
}

export function isCurrentCompositeOccurrence(item: BuyerLiveItem): boolean {
  const op = item.operational;
  if (!op || op.excluded === true) return false;
  if (text(op.trigger_kind) === 'TEMPORAL_PATTERN' || text(op.event_kind) === 'PATTERN_ALERT') {
    return false;
  }
  if (text(op.alert_class) !== 'Composite' && text(op.incident_type) !== 'composite_alert') {
    if (!text(op.incident_id).startsWith('ci1_')) return false;
  }
  return isOpenCompositeStatus(text(op.operational_status));
}

export function overviewConditionRowFromComposite(
  item: BuyerLiveItem,
): OverviewConditionRow | null {
  const detail = presentCompositeAlertDetail(item);
  const subject = compositeCurrentSubjectFromItem(item);
  if (!detail || !subject || !isCurrentCompositeOccurrence(item)) return null;
  const occurredAt = occurrenceTime(item);
  const location = buyerLocation(item);
  const severity = text(detail.severity);
  return {
    conditionId: subject.conditionId,
    label: detail.ruleName,
    location,
    measurementState: 'current',
    latestReadingAt: occurredAt,
    attentionRequired: false,
    attentionReason: null,
    tone: 'limited',
    zoneId: null,
    cameraId: text(item.operational.camera_id) || subject.scopeId,
    deviceId: text(item.operational.device_id) || null,
    conditionFamily: 'COMPOSITE_ALERT',
    incidentType: 'composite_alert',
    cardShape: 'completed_without_result',
    completedAt: occurredAt || '',
    currentThrough: occurredAt || undefined,
    currentThroughAt: occurredAt,
    coverageLabel: '',
    resultLabel: occurredAt ? `Latest occurrence ${occurredAt}` : 'Latest occurrence',
    ageLabel: '',
    nextUpdate: '',
    measurementValue: null,
    supportingActivityLabel: occurredAt ? `Latest occurrence ${occurredAt}` : 'Open composite occurrence',
    bodyLine: [severity, location].filter(Boolean).join(' · '),
    stateChipLabel: severity && severity !== '—' ? severity : 'Open',
    subjectKind: COMPOSITE_SUBJECT_KIND,
    compositeIncidentId: detail.incidentId,
    compositeEvidencePath: compositeDetailHref(detail.incidentId),
  };
}

function newerOccurrence(left: BuyerLiveItem, right: BuyerLiveItem): BuyerLiveItem {
  const leftTs = occurrenceTime(left) || '';
  const rightTs = occurrenceTime(right) || '';
  if (rightTs > leftTs) return right;
  if (rightTs < leftTs) return left;
  return text(right.operational.incident_id) > text(left.operational.incident_id) ? right : left;
}

export function selectCurrentCompositeSubjects(items: BuyerLiveItem[]): BuyerLiveItem[] {
  const latest = new Map<string, BuyerLiveItem>();
  for (const item of items) {
    if (!isCurrentCompositeOccurrence(item)) continue;
    const subject = compositeCurrentSubjectFromItem(item);
    if (!subject) continue;
    const previous = latest.get(subject.conditionId);
    latest.set(subject.conditionId, previous ? newerOccurrence(previous, item) : item);
  }
  return Array.from(latest.values()).sort((left, right) => {
    return text(right.operational.composite_triggered_at || right.operational.recorded_at)
      .localeCompare(text(left.operational.composite_triggered_at || left.operational.recorded_at));
  });
}

export function mergeOverviewV2CompositeNow(
  summary: OverviewDecisionSummary,
  items: BuyerLiveItem[] | null | undefined,
): OverviewDecisionSummary {
  const current = selectCurrentCompositeSubjects(items || []);
  const rows = current
    .map((item) => overviewConditionRowFromComposite(item))
    .filter((row): row is OverviewConditionRow => Boolean(row));
  if (!rows.length) return summary;
  const existingIds = new Set((summary.now.conditionRows || []).map((row) => row.conditionId));
  const additions = rows.filter((row) => !existingIds.has(row.conditionId));
  if (!additions.length) return summary;
  return {
    ...summary,
    now: {
      ...summary.now,
      conditionRows: [...(summary.now.conditionRows || []), ...additions],
    },
  };
}

export function compositeDetailHref(incidentId: string): string {
  return `/live?incident_id=${encodeURIComponent(text(incidentId))}`;
}
